import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminSearchInput, FiltersBackground, PageHeader, PageMain } from "components/molecules";
import { routes } from "consts";
import { IDropdownOption, MultiSelect, PaziaLoader, Stars } from "components/atoms";
import {
  PaziaModal,
  TanTable,
  useFilters,
  usePagination,
  useSorting,
  TanTableColumn,
} from "components/molecules";
import { formatDate } from "utils";
import { useCoursesOptions, useUserSurveyById } from "api/hooks";
import { useUserSurveys } from "api/hooks/useUserSurveys";
import { UserSurveyApi } from "api/types";
import { ModalPadding } from "components/templates/Modals/ModalPadding";
import { ModalHeader } from "components/templates/Modals/ModalHeader";

export const UserSurveysPage = (): JSX.Element | null => {
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const { isLoading: loadingDetail, survey: selectedSurvey } = useUserSurveyById(selectedId);
  const { searchPhrase, filter, setSearchPhrase, getInitFilterValues, changeFilter } = useFilters("userSurveys");
  const { limit, onPaginationChange, skip, pagination } = usePagination();
  const { sorting, onSortingChange, fieldWithOrder } = useSorting();

  const { isFetching, isLoading, userSurveys, total, invalidate } = useUserSurveys(
    skip,
    limit,
    searchPhrase,
    filter,
    fieldWithOrder,
  );

  useEffect(() => {
    invalidate();
  }, [sorting, pagination, searchPhrase, filter]);

  const { isLoading: loadingCourses, options: coursesOptions } = useCoursesOptions();

  const columns = React.useMemo<TanTableColumn<UserSurveyApi>[]>(() => cols, []);
  return (
    <PageMain>
      <PageHeader button={{ title: "Konfiguruj ankiety", onClick: () => navigate(routes.SURVEYS) }}>
        Ankiety użytkowników
      </PageHeader>

      <FiltersBackground className="mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <AdminSearchInput onSearch={(searchPhrase: string) => setSearchPhrase(searchPhrase)} className="self-end" />

          <MultiSelect
            label="Kurs"
            name="courses"
            defaultValue={getInitFilterValues("courses")}
            options={coursesOptions}
            onChange={(opts: IDropdownOption[]) =>
              changeFilter("courses", opts.length === 0 ? "" : `courses in (${opts.map((opt) => opt.value).join(",")})`)
            }
            isLoading={loadingCourses}
            className="grow"
          />
        </div>
      </FiltersBackground>
      <TanTable
        data={mapTableData(userSurveys)}
        columns={columns}
        total={total}
        loading={isLoading}
        fetching={isFetching}
        onPaginationChange={onPaginationChange}
        onSortingChange={onSortingChange}
        pagination={pagination}
        sorting={sorting}
        onRowClick={(row) => setSelectedId(row.userSurveyId)}
      />

      <PaziaModal
        id="user-survey-preview-modal"
        onCloseRequest={() => setSelectedId(null)}
        isOpen={selectedId !== null}
        size="lg"
      >
        <ModalPadding>
          <ModalHeader>{selectedSurvey?.survey.name}</ModalHeader>

          <div className="space-y-4">
            {/* User Info */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Uzytkownik
              </p>
              <p className="mt-1 font-medium text-slate-900 dark:text-slate-100">
                {selectedSurvey?.user.firstName} {selectedSurvey?.user.surname}
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-400">{selectedSurvey?.user.email}</p>
            </div>

            {/* Course Info */}
            {selectedSurvey?.course && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Kurs</p>
                <p className="mt-1 font-medium text-slate-900 dark:text-slate-100">{selectedSurvey.course.name}</p>
              </div>
            )}

            {/* Rating */}
            {selectedSurvey?.courseRating && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Ocena
                </p>
                <div className="mt-1 flex items-center gap-2">
                  <Stars numberOfStars={selectedSurvey.courseRating.rating} />
                  <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
                    ({selectedSurvey.courseRating.rating}/5)
                  </span>
                </div>
              </div>
            )}

            {/* Comment */}
            {selectedSurvey?.courseRating?.comment && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Komentarz
                </p>
                <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-slate-700 dark:text-slate-300">
                  {selectedSurvey.courseRating.comment}
                </p>
              </div>
            )}

            {/* No rating message */}
            {!selectedSurvey?.courseRating && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/50">
                <p className="text-sm text-slate-500 dark:text-slate-400">Brak oceny kursu w tej ankiecie</p>
              </div>
            )}

            {/* Date */}
            <div className="border-t border-slate-200 pt-4 dark:border-slate-700">
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Wypelniono: {selectedSurvey && formatDate(selectedSurvey.createdDate, true, true)}
              </p>
            </div>

            {/* Answers */}
            {loadingDetail && (
              <div className="flex justify-center pt-4">
                <PaziaLoader position="center" />
              </div>
            )}
            {!loadingDetail && selectedSurvey?.answers && selectedSurvey.answers.length > 0 && (
              <div className="border-t border-slate-200 pt-4 dark:border-slate-700">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-3">
                  Odpowiedzi
                </p>
                <div className="space-y-4">
                  {selectedSurvey.answers.map((answer, idx) => (
                    <div key={idx}>
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        {answer.question}
                      </p>
                      {answer.inputType === "rating" ||
                      answer.inputType === "course-rating" ||
                      answer.inputType === "trainer-rating" ? (
                        answer.rating != null ? (
                          <Stars numberOfStars={answer.rating} />
                        ) : (
                          <span className="text-sm text-slate-400">—</span>
                        )
                      ) : (
                        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400 whitespace-pre-wrap">
                          {answer.value ?? "—"}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </ModalPadding>
      </PaziaModal>
    </PageMain>
  );
};

const cols: TanTableColumn<UserSurveyApi>[] = [
  {
    id: "id",
    accessorKey: "userSurveyId",
    header: "ID",
    enableColumnFilter: false,
    size: 10,
    cell: (info) => info.getValue(),
  },
  {
    id: "userEmail",
    accessorKey: "userEmail",
    header: "Email",
    enableColumnFilter: false,
    cell: (info) => info.getValue(),
  },
  {
    id: "courseName",
    accessorKey: "courseName",
    header: "Kurs",
    size: 250,
    enableColumnFilter: false,
    cell: (info) => info.getValue(),
  },
  {
    id: "surveyName",
    accessorKey: "surveyName",
    header: "Ankieta",
    enableColumnFilter: false,
    size: 250,
    cell: (info) => info.getValue(),
  },
  {
    id: "rating",
    accessorKey: "rating",
    header: "Ocena",
    enableColumnFilter: false,
    cell: (info) => {
      return info.getValue() ? <Stars numberOfStars={info.getValue<number>()} /> : null;
    },
  },
  {
    id: "createdDate",
    accessorKey: "createdDate",
    header: "Data utworzenia",
    enableColumnFilter: false,
    cell: (info) => info.getValue(),
  },
];

const mapTableData = (data: UserSurveyApi[]): UserSurveyTableData[] => {
  return data.map((x) => ({
    userSurveyId: x.userSurveyId,
    userName: x.user.firstName + " " + x.user.surname,
    userEmail: x.user.email,
    surveyName: x.survey.name,
    createdDate: formatDate(x.createdDate),
    rating: x.courseRating?.rating,
    courseName: x.course?.name,
  }));
};

type UserSurveyTableData = {
  userSurveyId: number;
  userName: string;
  userEmail: string;
  surveyName: string;
  createdDate: string;
  rating?: number;
  courseName?: string;
};
